import { MenuItem, TextField, TextFieldProps } from '@mui/material';
import { L } from 'harmony-language';
import React from 'react';
import { useCargoTypes } from '../../../api/queries/use-cargo-types';
import { CargoType } from '../../../types';
import { cargoTypesToCategoryGroups } from '../../../utils/data-mapping';
import { DynamicTranslate } from '../../../constants/constants';

// Object containing a 'header' object for each group of cargo type
// along with the cargo types in that group
type CargoTypeDropdownItem = CargoType | {
    id: string; // string for group headers, number for cargo types
    headerLabel: string; // Only on the header object
}

type SelectCargoTypeProps = TextFieldProps & {
    cargoType: CargoType | null;
    handleChange: (cargoType: CargoType) => void;
    filterSelector?: (cargoType: CargoType) => boolean;
}

export const SelectCargoType = (props: SelectCargoTypeProps) => {
    const { cargoType, handleChange, filterSelector, ...rest } = props;
    const { data = [] } = useCargoTypes();
    const cargoTypes = React.useMemo(() => {
        if(filterSelector){
            return data?.filter(filterSelector);
        }
        return data;
    }, [data, filterSelector]);

    const cargoTypeGroups = React.useMemo(() => {
        return cargoTypesToCategoryGroups(cargoTypes);
    }, [cargoTypes]);

    const cargoTypeList = Object.keys(cargoTypeGroups).sort().map(group => {
        return cargoTypeGroups[group].reduce((acc: CargoTypeDropdownItem[], cargoType) => {
            return acc.concat(cargoType);
        }, [{ id: group, headerLabel: group }]);
    }).flat();

    return (
        <TextField
            variant='standard'
            id='cargo-type'
            label={L.cargoType()}
            select
            value={cargoType?.id || ''}
            SelectProps={{
                MenuProps: {
                    MenuListProps: {
                        disablePadding: true
                    }
                }
            }}
            onChange={(e) => {
                const value = e.target.value;
                const cargoType = cargoTypes.find(l => l.id === Number(value))!;

                handleChange(cargoType);
            }}
            {...rest}>
            {cargoTypeList.map((c) => {
                const isGroupHeader = 'headerLabel' in c;
                return (
                    <MenuItem key={c.id}
                        value={c.id}
                        disabled={isGroupHeader}
                        style={{
                            opacity: isGroupHeader ? 1 : undefined,
                            ...(isGroupHeader && {
                                backgroundColor: '#424242',
                                color: 'white',
                                display: 'block',
                                textAlign: 'center',
                            }),
                        }}
                    >
                        {isGroupHeader ? c.headerLabel : DynamicTranslate(`cargoValue${c.id}`)}
                    </MenuItem>
                );
            })}
        </TextField>
    );
};
